import { lazyRouteComponent, notFound } from "@tanstack/react-router"
import { formatCalendarDate } from "@ui/lib/date"
import type { FunctionComponent } from "react"

import { isLegalVersionEffective, UNRELEASED } from "./content-metadata"
import { todayUtc } from "./legal-paths"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MdxComponent = FunctionComponent<{ components?: Record<string, any> }> & {
  preload?: () => Promise<void>
}

export type LegalModules = Record<string, MdxComponent>

/**
 * Wraps a document's lazy `import.meta.glob` once, at module scope, so each
 * version is its own chunk and keeps one component across renders.
 */
export function lazyLegalModules(glob: Record<string, () => Promise<unknown>>): LegalModules {
  return Object.fromEntries(
    Object.entries(glob).map(([path, load]) => [
      path,
      lazyRouteComponent(load as () => Promise<{ default: MdxComponent }>),
    ]),
  )
}

export interface LegalVersion {
  /** YYYY-MM-DD for released versions, or "unreleased" for the working draft. */
  date: string
  /** Human-friendly date, or "Unreleased" for the working draft. */
  dateFormatted: string
  /** Short date for banners, e.g. "25 Nov 2026", or "Unreleased". */
  dateShort: string
  /** True when this version has no shipped date yet. */
  isUnreleased: boolean
  /**
   * True once the version is in effect (00:00 UTC on its date) as of the
   * resolution date. Only effective versions can be served as the live
   * document; the unreleased draft is never effective.
   */
  isEffective: boolean
  Content: MdxComponent
}

/** Resolves versions as of `asOf` (YYYY-MM-DD in UTC; see `todayUtc`). */
export function resolveLegalVersions(modules: LegalModules, asOf: string): LegalVersion[] {
  return Object.entries(modules)
    .map(([path, Content]) => {
      const filename = path
        .split("/")
        .pop()!
        .replace(/\.(mdx?|md)$/, "")
      const isUnreleased = filename === UNRELEASED
      return {
        date: filename,
        dateFormatted: isUnreleased ? "Unreleased" : formatCalendarDate(filename, "longDateGB"),
        isUnreleased,
        dateShort: isUnreleased ? "Unreleased" : formatCalendarDate(filename, "dateGB"),
        isEffective: isLegalVersionEffective(filename, asOf),
        Content,
      }
    })
    .sort((a, b) => {
      // Unreleased sorts first; otherwise newest date first.
      if (a.isUnreleased) return -1
      if (b.isUnreleased) return 1
      return b.date.localeCompare(a.date)
    })
}

export function findLegalVersion(versions: LegalVersion[], date: string): LegalVersion | undefined {
  return versions.find((v) => v.date === date)
}

/**
 * The live, published version shown at the base path: the newest effective
 * (dated, not future) version. Returns undefined when only an unreleased draft
 * or future-dated versions exist, so a not-yet-effective document is never
 * served as the authoritative one. Those remain viewable at
 * `{basePath}/{date}` (a noindex preview).
 */
export function currentLegalVersion(versions: LegalVersion[]): LegalVersion | undefined {
  return versions.find((v) => v.isEffective)
}

// Route loaders resolve per request, so a page switches at 00:00 UTC on the
// effective date, and load the shown version's chunk so it renders without
// suspending.

/** Loader for a document's base path: the version in effect today. */
export async function loadCurrentLegalVersion(modules: LegalModules) {
  const asOf = todayUtc()
  const current = currentLegalVersion(resolveLegalVersions(modules, asOf))
  if (!current) throw notFound()
  await current.Content.preload?.()
  return { asOf }
}

/** Loader for a dated version's page. */
export async function loadLegalVersion(modules: LegalModules, date: string) {
  const asOf = todayUtc()
  const version = findLegalVersion(resolveLegalVersions(modules, asOf), date)
  if (!version) throw notFound()
  await version.Content.preload?.()
  return { asOf, date: version.date, dateFormatted: version.dateFormatted }
}
